import { useCallback, useRef } from "react";

type SidebarEvent = "NAVIGATE_NEXT" | "NAVIGATE_PREVIOUS" | "CLOSE_SIDEBAR";

interface EventEmitter {
  on: (event: SidebarEvent, handler: () => void) => void;
  off: (event: SidebarEvent, handler: () => void) => void;
  emit: (event: SidebarEvent) => void;
}

class SimpleSidebarEventEmitter implements EventEmitter {
  private listeners = new Map<SidebarEvent, Set<() => void>>();

  on(event: SidebarEvent, handler: () => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: SidebarEvent, handler: () => void): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  emit(event: SidebarEvent): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler());
    }
  }
}

// Global event emitter instance
const globalSidebarEventEmitter = new SimpleSidebarEventEmitter();

/**
 * Hook for emitting and subscribing to table sidebar events.
 * Uses a global event emitter to enable communication between components.
 */
export function useTableDetailSidebarNavigationEvents(): {
  emit: (eventType: SidebarEvent) => void;
  subscribe: (eventType: SidebarEvent, handler: () => void) => () => void;
} {
  const emitterRef = useRef(globalSidebarEventEmitter);

  const emit = useCallback((eventType: SidebarEvent) => {
    emitterRef.current.emit(eventType);
  }, []);

  const subscribe = useCallback(
    (eventType: SidebarEvent, handler: () => void) => {
      const emitter = emitterRef.current;
      emitter.on(eventType, handler);

      // Return cleanup function
      return () => {
        emitter.off(eventType, handler);
      };
    },
    [],
  );

  return { emit, subscribe };
}
